import type { ReactNode } from 'react';
import { SORT_LABELS, nextSort } from './leadSort';
import type { LeadSort, SortColumn } from './leadSort';

/**
 * A column header you can click to reorder the table.
 *
 * ── Why it is a button and not a header with a click handler ───────────────
 *
 * Sorting is an action, and an action a keyboard cannot reach is an action
 * half the team does not have. The `aria-sort` goes on the `th` in the page,
 * which is where a screen reader looks for it; the button carries the label
 * saying what the NEXT click will do, because "Name, sorted A to Z" tells you
 * the state and not the offer.
 *
 * ── The arrow only appears on the sorted column ────────────────────────────
 *
 * docs/design-rule.md, mechanism three: weight is spent on the exception. An
 * arrow on all five sortable headers would be five marks saying "this is
 * ordinary", and the one that is actually doing something would be lost among
 * them. Unsorted headers look exactly like the ones that cannot sort, and the
 * affordance shows on hover — which is safe here because the table is
 * readable and complete without ever discovering it.
 */
export function SortHeader({
  column,
  sort,
  onSort,
  children,
}: {
  column: SortColumn;
  sort: LeadSort | null;
  onSort: (next: LeadSort | null) => void;
  children: ReactNode;
}) {
  const active = sort !== null && sort.column === column;
  const upcoming = nextSort(sort, column);
  return (
    <button
      type="button"
      className={active ? 'sort-header sort-header--on' : 'sort-header'}
      // What the click will DO, not what the column currently is. The state is
      // already announced by aria-sort on the cell around this.
      title={
        upcoming === null ? 'Remove the sort' : `Sort: ${SORT_LABELS[column][upcoming.direction]}`
      }
      onClick={() => onSort(upcoming)}
    >
      {children}
      {active ? (
        <span className="sort-header-caret" aria-hidden="true">
          {sort.direction === 'asc' ? '↑' : '↓'}
        </span>
      ) : null}
    </button>
  );
}

/** What `aria-sort` on the `th` should say for this column. */
export function ariaSortFor(
  sort: LeadSort | null,
  column: SortColumn,
): 'ascending' | 'descending' | 'none' {
  if (sort === null || sort.column !== column) {
    return 'none';
  }
  return sort.direction === 'asc' ? 'ascending' : 'descending';
}
